import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Container } from "../../components/ui/Container";
import { Stack } from "../../components/ui/Stack";
import { Heading } from "../../components/ui/Heading";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Spinner } from "../../components/ui/Spinner";
import { Alert } from "../../components/ui/Alert";
import { Text } from "../../components/ui/Text";
import { PasswordInput } from "../../components/ui/PasswordInput";
import { useStore } from "../../context/StoreContext";
import { stripeConfigApi, paypalConfigApi, api } from "../../services/api";

  interface Location {
  _id: string;
  country: string;
  countryCode: string;
  currency: string;
  currencySymbol: string;
  stripeConfig?: {
    publishableKey?: string;
    paymentMethodTypes?: string[];
    isConfigured: boolean;
  };
  paypalConfig?: {
    clientId?: string;
    isConfigured: boolean;
  };
}

// Get payment gateway setup instructions
const getPaymentGatewayInstructions = (gateway: string) => {
  if (gateway === "stripe") {
    return {
      steps: [
        {
          title: "1. Create a Stripe Account",
          description: "Sign up for a Stripe account at stripe.com if you don't have one already",
          link: "https://stripe.com/",
          linkText: "Stripe Website",
        },
        {
          title: "2. Get Your API Keys",
          description: "Go to Developers > API keys in your Stripe dashboard. Copy your Publishable key and Secret key. Use Test keys for testing, Live keys for production.",
          link: "https://dashboard.stripe.com/apikeys",
          linkText: "Stripe API Keys",
        },
        {
          title: "3. Enter Your Keys",
          description: "Paste your Publishable key and Secret key in the form below. Make sure both keys are from the same mode (test or live).",
        },
      ],
    };
  }
  if (gateway === "paypal") {
    return {
      steps: [
        {
          title: "1. Create a PayPal Business Account",
          description: "Sign up for a PayPal Business account if you don't have one already",
          link: "https://www.paypal.com/business",
          linkText: "PayPal Business",
        },
        {
          title: "2. Create a PayPal App",
          description: "Go to PayPal Developer Dashboard > My Apps & Credentials. Create a new app or use an existing one. For testing, use the Sandbox app credentials.",
          link: "https://developer.paypal.com/dashboard/applications/sandbox",
          linkText: "PayPal Developer Dashboard",
        },
        {
          title: "3. Get Your Credentials",
          description: "Copy the Client ID and Client Secret from your app. Sandbox credentials are for testing, Live credentials are for production.",
          link: "https://developer.paypal.com/docs/api-basics/manage-apps/",
          linkText: "PayPal App Management Guide",
        },
        {
          title: "4. Enter Your Credentials",
          description: "Paste your Client ID and Client Secret in the form below.",
        },
      ],
    };
  }
  return { steps: [] };
};

export const PaymentGatewaySettings: React.FC = () => {
  const { t } = useTranslation();
  const { isPluginEnabled, loading: storeLoading } = useStore();

  // Locations state
  const [locations, setLocations] = useState<Location[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);

  // Stripe configuration state for selected location
  const [stripePublishableKey, setStripePublishableKey] = useState("");
  const [stripeSecretKey, setStripeSecretKey] = useState("");
  const [stripeWebhookSecret, setStripeWebhookSecret] = useState("");
  const [stripePaymentMethodTypes, setStripePaymentMethodTypes] = useState<string[]>(["card"]);
  const [savingStripe, setSavingStripe] = useState(false);
  const [testingStripe, setTestingStripe] = useState(false);
  const [stripeError, setStripeError] = useState<string | null>(null);
  const [stripeSuccess, setStripeSuccess] = useState<string | null>(null);
  const [stripeConfigLoading, setStripeConfigLoading] = useState(false);
  const [showStripeHelp, setShowStripeHelp] = useState(false);

  // PayPal configuration state for selected location
  const [paypalClientId, setPaypalClientId] = useState("");
  const [paypalClientSecret, setPaypalClientSecret] = useState("");
  const [savingPayPal, setSavingPayPal] = useState(false);
  const [testingPayPal, setTestingPayPal] = useState(false);
  const [paypalError, setPaypalError] = useState<string | null>(null);
  const [paypalSuccess, setPaypalSuccess] = useState<string | null>(null);
  const [paypalConfigLoading, setPaypalConfigLoading] = useState(false);
  const [showPayPalHelp, setShowPayPalHelp] = useState(false);

  const isOnlinePaymentEnabled = isPluginEnabled("online-payment");
  const loading = storeLoading;

  // Fetch locations
  useEffect(() => {
    const fetchLocations = async () => {
      if (!isOnlinePaymentEnabled) {
        return;
      }
      setLoadingLocations(true);
      try {
        const response = await api.get("/locations/admin?isActive=true");
        const fetchedLocations = response.data.locations || [];
        setLocations(fetchedLocations);
      } catch (err: any) {
        console.error("Error fetching locations:", err);
      } finally {
        setLoadingLocations(false);
      }
    };
    fetchLocations();
  }, [isOnlinePaymentEnabled]);

  // Load Stripe config when location is selected
  useEffect(() => {
    if (!selectedLocationId) {
      setStripePublishableKey("");
      setStripeSecretKey("");
      setStripeWebhookSecret("");
      setStripePaymentMethodTypes(["card"]);
      return;
    }

    setStripeConfigLoading(true);
    const loadStripeConfig = async () => {
      try {
        const response = await stripeConfigApi.getLocationStripeConfig(selectedLocationId);
        if (response.data?.publishableKey) {
          setStripePublishableKey(response.data.publishableKey);
        } else {
          setStripePublishableKey("");
        }
        if (response.data?.paymentMethodTypes && response.data.paymentMethodTypes.length > 0) {
          setStripePaymentMethodTypes(response.data.paymentMethodTypes);
        } else {
          setStripePaymentMethodTypes(["card"]);
        }
      } catch (err: any) {
        // Ignore errors - config might not exist yet
        setStripePublishableKey("");
        setStripePaymentMethodTypes(["card"]);
      } finally {
        setStripeConfigLoading(false);
      }
    };
    loadStripeConfig();
  }, [selectedLocationId]);

  // Load PayPal config when location is selected
  useEffect(() => {
    if (!selectedLocationId) {
      setPaypalClientId("");
      setPaypalClientSecret("");
      return;
    }

    setPaypalConfigLoading(true);
    const loadPayPalConfig = async () => {
      try {
        const response = await paypalConfigApi.getLocationPayPalConfig(selectedLocationId);
        if (response.data?.clientId) {
          setPaypalClientId(response.data.clientId);
        } else {
          setPaypalClientId("");
        }
      } catch (err: any) {
        // Ignore errors - config might not exist yet
        setPaypalClientId("");
      } finally {
        setPaypalConfigLoading(false);
      }
    };
    loadPayPalConfig();
  }, [selectedLocationId]);

  // Update from selected location when it changes
  useEffect(() => {
    if (selectedLocationId) {
      const location = locations.find((loc) => loc._id === selectedLocationId);
      if (location?.stripeConfig?.publishableKey) {
        setStripePublishableKey(location.stripeConfig.publishableKey);
      }
      if (location?.stripeConfig?.paymentMethodTypes && location.stripeConfig.paymentMethodTypes.length > 0) {
        setStripePaymentMethodTypes(location.stripeConfig.paymentMethodTypes);
      } else {
        setStripePaymentMethodTypes(["card"]);
      }
      if (location?.paypalConfig?.clientId) {
        setPaypalClientId(location.paypalConfig.clientId);
      }
    }
  }, [locations, selectedLocationId]);

  const handleLocationSelect = (locationId: string) => {
    setSelectedLocationId(locationId);
    setStripeError(null);
    setStripeSuccess(null);
    setPaypalError(null);
    setPaypalSuccess(null);
  };

  const handleStripeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLocationId) {
      return;
    }

    setStripeError(null);
    setStripeSuccess(null);

    // Check if this is a new configuration (no existing config)
    const isNewConfig = !isStripeConfigured;

    // For new configs, both keys are required
    if (isNewConfig && (!stripePublishableKey || !stripeSecretKey)) {
      setStripeError(t("admin.paymentGatewaySettings.stripe.errors.keysRequired"));
      return;
    }

    // For updates, at least one field must be provided
    if (!isNewConfig && !stripePublishableKey && !stripeSecretKey && !stripeWebhookSecret && stripePaymentMethodTypes.length === 0) {
      setStripeError("Please update at least one field");
      return;
    }

    setSavingStripe(true);
    try {
      // Build request payload with only provided fields
      const payload: {
        publishableKey?: string;
        secretKey?: string;
        webhookSecret?: string | null;
        paymentMethodTypes?: string[];
      } = {};

      // Only include publishable key if provided
      if (stripePublishableKey && stripePublishableKey.trim()) {
        payload.publishableKey = stripePublishableKey.trim();
      }

      // Only include secret key if provided (not empty)
      if (stripeSecretKey && stripeSecretKey.trim()) {
        payload.secretKey = stripeSecretKey.trim();
      }

      // Include webhook secret: empty string means clear it, undefined means don't send
      if (stripeWebhookSecret !== undefined) {
        payload.webhookSecret = stripeWebhookSecret.trim() || null;
      }

      // Include payment method types if provided
      if (stripePaymentMethodTypes.length > 0) {
        payload.paymentMethodTypes = stripePaymentMethodTypes;
      }

      await stripeConfigApi.saveLocationStripeConfig(selectedLocationId, payload);
      setStripeSuccess(t("admin.paymentGatewaySettings.stripe.success.save"));
      setStripeSecretKey(""); // Clear secret key for security
      setStripeWebhookSecret(""); // Clear webhook secret
      
      // Refresh locations to update config status
      const response = await api.get("/locations/admin?isActive=true");
      setLocations(response.data.locations || []);
    } catch (err: any) {
      setStripeError(err.response?.data?.error || t("admin.paymentGatewaySettings.stripe.errors.save"));
    } finally {
      setSavingStripe(false);
    }
  };

  const handleDeleteStripeConfig = async () => {
    if (!selectedLocationId) {
      return;
    }

    if (!window.confirm(t("admin.paymentGatewaySettings.stripe.confirmRemove"))) {
      return;
    }

    setStripeError(null);
    setStripeSuccess(null);
    setSavingStripe(true);
    try {
      await stripeConfigApi.deleteLocationStripeConfig(selectedLocationId);
      setStripeSuccess(t("admin.paymentGatewaySettings.stripe.success.remove"));
      setStripePublishableKey("");
      setStripeSecretKey("");
      setStripeWebhookSecret("");
      setStripePaymentMethodTypes(["card"]);
      
      // Refresh locations to update config status
      const response = await api.get("/locations/admin?isActive=true");
      setLocations(response.data.locations || []);
    } catch (err: any) {
      setStripeError(err.response?.data?.error || t("admin.paymentGatewaySettings.stripe.errors.remove"));
    } finally {
      setSavingStripe(false);
    }
  };

  const handleTestStripeConnection = async () => {
    if (!selectedLocationId) {
      return;
    }

    if (!stripePublishableKey || !stripeSecretKey) {
      setStripeError(t("admin.paymentGatewaySettings.stripe.errors.testKeysRequired"));
      return;
    }

    setTestingStripe(true);
    setStripeError(null);
    try {
      // Test by trying to save (which validates the keys)
      await stripeConfigApi.saveLocationStripeConfig(selectedLocationId, {
        publishableKey: stripePublishableKey.trim(),
        secretKey: stripeSecretKey.trim(),
      });
      setStripeSuccess(t("admin.paymentGatewaySettings.stripe.success.testValid"));
    } catch (err: any) {
      setStripeError(err.response?.data?.error || t("admin.paymentGatewaySettings.stripe.errors.validate"));
    } finally {
      setTestingStripe(false);
    }
  };

  const selectedLocation = selectedLocationId ? locations.find((loc) => loc._id === selectedLocationId) : null;
  const isStripeConfigured = selectedLocation?.stripeConfig?.isConfigured ?? false;
  const isPayPalConfigured = selectedLocation?.paypalConfig?.isConfigured ?? false;

  const handlePayPalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLocationId) {
      return;
    }

    setPaypalError(null);
    setPaypalSuccess(null);

    if (!paypalClientId || !paypalClientSecret) {
      setPaypalError("Client ID and Client Secret are required");
      return;
    }

    setSavingPayPal(true);
    try {
      await paypalConfigApi.saveLocationPayPalConfig(selectedLocationId, {
        clientId: paypalClientId.trim(),
        clientSecret: paypalClientSecret.trim(),
      });
      setPaypalSuccess("PayPal configuration saved successfully");
      setPaypalClientSecret(""); // Clear secret for security
      
      // Refresh locations to update config status
      const response = await api.get("/locations/admin?isActive=true");
      setLocations(response.data.locations || []);
    } catch (err: any) {
      setPaypalError(err.response?.data?.error || "Failed to save PayPal configuration");
    } finally {
      setSavingPayPal(false);
    }
  };

  const handleDeletePayPalConfig = async () => {
    if (!selectedLocationId) {
      return;
    }

    if (!window.confirm("Are you sure you want to remove PayPal configuration?")) {
      return;
    }

    setPaypalError(null);
    setPaypalSuccess(null);
    setSavingPayPal(true);
    try {
      await paypalConfigApi.deleteLocationPayPalConfig(selectedLocationId);
      setPaypalSuccess("PayPal configuration removed successfully");
      setPaypalClientId("");
      setPaypalClientSecret("");
      
      // Refresh locations to update config status
      const response = await api.get("/locations/admin?isActive=true");
      setLocations(response.data.locations || []);
    } catch (err: any) {
      setPaypalError(err.response?.data?.error || "Failed to remove PayPal configuration");
    } finally {
      setSavingPayPal(false);
    }
  };

  const handleTestPayPalConnection = async () => {
    if (!selectedLocationId) {
      return;
    }

    if (!paypalClientId || !paypalClientSecret) {
      setPaypalError("Client ID and Client Secret are required");
      return;
    }

    setTestingPayPal(true);
    setPaypalError(null);
    try {
      // Test by trying to save (which validates the credentials)
      await paypalConfigApi.saveLocationPayPalConfig(selectedLocationId, {
        clientId: paypalClientId.trim(),
        clientSecret: paypalClientSecret.trim(),
      });
      setPaypalSuccess("PayPal credentials are valid");
    } catch (err: any) {
      setPaypalError(err.response?.data?.error || "Failed to validate PayPal credentials");
    } finally {
      setTestingPayPal(false);
    }
  };

  if (loading || loadingLocations) {
    return (
      <Container>
        <Stack spacing="lg">
          <Spinner />
        </Stack>
      </Container>
    );
  }

  if (!isOnlinePaymentEnabled) {
    return (
      <Container>
        <Stack spacing="lg">
          <Heading size="xl">{t("admin.paymentGatewaySettings.title")}</Heading>
          <Alert variant="warning">
            <Text>
              {t("admin.paymentGatewaySettings.pluginRequired")}
            </Text>
          </Alert>
        </Stack>
      </Container>
    );
  }

  return (
    <Container>
      <Stack spacing="lg">
        <div>
          <Heading size="xl">{t("admin.paymentGatewaySettings.title")}</Heading>
          <Text className="text-gray-600 dark:text-gray-400 mt-2">
            {t("admin.paymentGatewaySettings.description")} Configure payment gateway settings for each country/location.
          </Text>
        </div>

        {/* Location Selection - Shared for all gateways */}
        <Card>
          <Stack spacing="md">
            <div>
              <label htmlFor="locationSelect" className="block text-sm font-medium mb-2">
                Select Location/Country
              </label>
              <select
                id="locationSelect"
                value={selectedLocationId || ""}
                onChange={(e) => handleLocationSelect(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400"
              >
                <option value="">-- Select a location --</option>
                {locations.map((location) => (
                  <option key={location._id} value={location._id}>
                    {location.country} ({location.countryCode}) - {location.currencySymbol} {location.currency}
                    {(location.stripeConfig?.isConfigured || location.paypalConfig?.isConfigured) ? " ✓" : ""}
                  </option>
                ))}
              </select>
            </div>
            {selectedLocation && (
              <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                <div>
                  <Text className="font-medium">
                    {selectedLocation.country} ({selectedLocation.countryCode})
                  </Text>
                  <Text className="text-sm text-gray-600 dark:text-gray-400">
                    Currency: {selectedLocation.currencySymbol} {selectedLocation.currency}
                  </Text>
                </div>
                <div className="flex items-center gap-2">
                  {isStripeConfigured && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Stripe ✓
                    </span>
                  )}
                  {isPayPalConfigured && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      PayPal ✓
                    </span>
                  )}
                </div>
              </div>
            )}
          </Stack>
        </Card>

        {!selectedLocation && (
          <Alert variant="info">
            <Text>
              Please select a location above to configure payment gateway settings.
            </Text>
          </Alert>
        )}

        <Card>
          <Stack spacing="lg">
            <div>
              <Heading size="lg">Stripe</Heading>
              <Text className="text-gray-600 dark:text-gray-400 mt-1">
                {t("admin.paymentGatewaySettings.stripe.description")}
              </Text>
            </div>

            {selectedLocation ? (
              <>
                {isStripeConfigured && (
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {t("admin.paymentGatewaySettings.stripe.configured")}
                    </span>
                    <span className="text-xs text-gray-500">
                      {stripePublishableKey?.startsWith("pk_test_") ? t("admin.paymentGatewaySettings.stripe.testMode") : t("admin.paymentGatewaySettings.stripe.liveMode")}
                    </span>
                  </div>
                )}

                {stripeError && <Alert variant="error">{stripeError}</Alert>}
                {stripeSuccess && <Alert variant="success">{stripeSuccess}</Alert>}

                {/* Help Instructions */}
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <button
                      type="button"
                      onClick={() => setShowStripeHelp(!showStripeHelp)}
                      className="text-sm text-blue-600 hover:text-blue-800 underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded"
                    >
                      {showStripeHelp ? "Hide help" : "Help - How to get Stripe credentials"}
                    </button>
                  </div>
                  {showStripeHelp && (
                    <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                      <h4 className="text-sm font-semibold text-blue-800 mb-2">
                        How to Get Your Stripe Credentials
                      </h4>
                      <div className="space-y-3 text-sm text-blue-700">
                        {getPaymentGatewayInstructions("stripe").steps.map((step, idx) => (
                          <div key={idx}>
                            <p className="font-medium">{step.title}</p>
                            <p className="text-blue-600">{step.description}</p>
                            {step.link && (
                              <a
                                href={step.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 underline hover:text-blue-800 inline-flex items-center gap-1"
                              >
                                {step.linkText} →
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <form onSubmit={handleStripeSubmit}>
                  <Stack spacing="md">
                    <div>
                      <label htmlFor="stripePublishableKey" className="block text-sm font-medium mb-1">
                        {t("admin.paymentGatewaySettings.stripe.publishableKey")}
                        {isStripeConfigured && <span className="text-gray-500 text-xs ml-1">(optional)</span>}
                      </label>
                      <input
                        type="text"
                        id="stripePublishableKey"
                        value={stripePublishableKey}
                        onChange={(e) => setStripePublishableKey(e.target.value)}
                        placeholder={t("admin.paymentGatewaySettings.stripe.publishableKeyPlaceholder")}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400"
                        disabled={stripeConfigLoading}
                      />
                      {isStripeConfigured && (
                        <Text size="xs" className="text-gray-500 mt-1">
                          Leave empty to keep existing publishable key
                        </Text>
                      )}
                    </div>

                    <div>
                      <label htmlFor="stripeSecretKey" className="block text-sm font-medium mb-1">
                        {t("admin.paymentGatewaySettings.stripe.secretKey")}
                        {isStripeConfigured && <span className="text-gray-500 text-xs ml-1">(optional)</span>}
                      </label>
                      <PasswordInput
                        id="stripeSecretKey"
                        value={stripeSecretKey}
                        onChange={(e) => setStripeSecretKey(e.target.value)}
                        placeholder={t("admin.paymentGatewaySettings.stripe.secretKeyPlaceholder")}
                        helperText={isStripeConfigured ? "Leave empty to keep existing secret key" : t("admin.paymentGatewaySettings.stripe.secretKeyHelp")}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400"
                        disabled={stripeConfigLoading}
                      />
                    </div>

                    <div>
                      <PasswordInput
                        id="stripeWebhookSecret"
                        label={t("admin.paymentGatewaySettings.stripe.webhookSecret")}
                        value={stripeWebhookSecret}
                        onChange={(e) => setStripeWebhookSecret(e.target.value)}
                        placeholder={t("admin.paymentGatewaySettings.stripe.webhookSecretPlaceholder")}
                        helperText={t("admin.paymentGatewaySettings.stripe.webhookSecretHelp")}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400"
                        disabled={stripeConfigLoading}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Payment Methods
                      </label>
                      <Text size="sm" className="text-gray-600 dark:text-gray-400 mb-3">
                        Select which payment methods to allow for this location. At least one method must be selected.
                      </Text>
                      <div className="space-y-2">
                        {[
                          { value: "card", label: "Credit/Debit Card" },
                          { value: "paypal", label: "PayPal" },
                          { value: "apple_pay", label: "Apple Pay" },
                          { value: "google_pay", label: "Google Pay" },
                          { value: "link", label: "Link" },
                          { value: "klarna", label: "Klarna" },
                          { value: "affirm", label: "Affirm" },
                          { value: "afterpay_clearpay", label: "Afterpay/Clearpay" },
                        ].map((method) => (
                          <label key={method.value} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={stripePaymentMethodTypes.includes(method.value)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setStripePaymentMethodTypes([...stripePaymentMethodTypes, method.value]);
                                } else {
                                  // Ensure at least one method is selected
                                  const newTypes = stripePaymentMethodTypes.filter((t) => t !== method.value);
                                  if (newTypes.length > 0) {
                                    setStripePaymentMethodTypes(newTypes);
                                  }
                                }
                              }}
                              className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                              disabled={stripeConfigLoading}
                            />
                            <Text size="sm">{method.label}</Text>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleTestStripeConnection}
                        loading={testingStripe}
                        disabled={testingStripe || !stripePublishableKey || !stripeSecretKey}
                      >
                        {t("admin.paymentGatewaySettings.stripe.testConnection")}
                      </Button>
                      <Button type="submit" loading={savingStripe} disabled={savingStripe || stripeConfigLoading}>
                        {t("admin.paymentGatewaySettings.stripe.saveConfiguration")}
                      </Button>
                      {isStripeConfigured && (
                        <Button
                          type="button"
                          variant="danger"
                          onClick={handleDeleteStripeConfig}
                          loading={savingStripe}
                          disabled={savingStripe}
                        >
                          {t("admin.paymentGatewaySettings.stripe.removeConfiguration")}
                        </Button>
                      )}
                    </div>
                  </Stack>
                </form>
              </>
            ) : (
              <Text className="text-gray-500 italic">
                Please select a location above to configure Stripe payment gateway.
              </Text>
            )}
          </Stack>
        </Card>

        {/* PayPal Configuration */}
        <Card>
          <Stack spacing="lg">
            <div>
              <Heading size="lg">PayPal</Heading>
              <Text className="text-gray-600 dark:text-gray-400 mt-1">
                Configure PayPal payment gateway settings for each country/location.
              </Text>
            </div>

            {selectedLocation ? (
              <>
                {isPayPalConfigured && (
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Configured
                    </span>
                    <span className="text-xs text-gray-500">
                      {paypalClientId?.includes("sandbox") || paypalClientId?.includes("SB") ? "Sandbox" : "Live"}
                    </span>
                  </div>
                )}

                {paypalError && <Alert variant="error">{paypalError}</Alert>}
                {paypalSuccess && <Alert variant="success">{paypalSuccess}</Alert>}

                {/* Help Instructions */}
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <button
                      type="button"
                      onClick={() => setShowPayPalHelp(!showPayPalHelp)}
                      className="text-sm text-blue-600 hover:text-blue-800 underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded"
                    >
                      {showPayPalHelp ? "Hide help" : "Help - How to get PayPal credentials"}
                    </button>
                  </div>
                  {showPayPalHelp && (
                    <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                      <h4 className="text-sm font-semibold text-blue-800 mb-2">
                        How to Get Your PayPal Credentials
                      </h4>
                      <div className="space-y-3 text-sm text-blue-700">
                        {getPaymentGatewayInstructions("paypal").steps.map((step, idx) => (
                          <div key={idx}>
                            <p className="font-medium">{step.title}</p>
                            <p className="text-blue-600">{step.description}</p>
                            {step.link && (
                              <a
                                href={step.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 underline hover:text-blue-800 inline-flex items-center gap-1"
                              >
                                {step.linkText} →
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <form onSubmit={handlePayPalSubmit}>
                  <Stack spacing="md">
                    <div>
                      <label htmlFor="paypalClientId" className="block text-sm font-medium mb-1">
                        Client ID
                      </label>
                      <input
                        type="text"
                        id="paypalClientId"
                        value={paypalClientId}
                        onChange={(e) => setPaypalClientId(e.target.value)}
                        placeholder="Enter PayPal Client ID"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400"
                        disabled={paypalConfigLoading}
                      />
                    </div>

                    <div>
                      <PasswordInput
                        id="paypalClientSecret"
                        label="Client Secret"
                        value={paypalClientSecret}
                        onChange={(e) => setPaypalClientSecret(e.target.value)}
                        placeholder="Enter PayPal Client Secret"
                        helperText={isPayPalConfigured ? "Leave blank to keep current secret, or enter new secret to update" : "Your PayPal Client Secret (will be encrypted)"}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400"
                        disabled={paypalConfigLoading}
                      />
                    </div>

                    <div className="flex gap-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleTestPayPalConnection}
                        loading={testingPayPal}
                        disabled={testingPayPal || !paypalClientId || !paypalClientSecret}
                      >
                        Test Connection
                      </Button>
                      <Button type="submit" loading={savingPayPal} disabled={savingPayPal || paypalConfigLoading}>
                        Save Configuration
                      </Button>
                      {isPayPalConfigured && (
                        <Button
                          type="button"
                          variant="danger"
                          onClick={handleDeletePayPalConfig}
                          loading={savingPayPal}
                          disabled={savingPayPal}
                        >
                          Remove Configuration
                        </Button>
                      )}
                    </div>
                  </Stack>
                </form>
              </>
            ) : (
              <Text className="text-gray-500 italic">
                Please select a location above to configure PayPal payment gateway.
              </Text>
            )}
          </Stack>
        </Card>
      </Stack>
    </Container>
  );
};
